<template>
  <div v-if="config.sender_approach || hasSteps || hasSampleEmails" class="details">
    <div v-if="config.sender_approach" class="detailRow">
      <span class="detailLabel">APPROACH</span>
      <span class="detailValue approach">{{ config.sender_approach }}</span>
    </div>
    <div v-if="hasSteps" class="detailRow">
      <span class="detailLabel">SEQUENCE</span>
      <SequenceSummary
        :steps="config.steps"
        :compact="true"
        :step-counts="stepCountsArray"
        :step-counts-ready="stepReadyCountsArray"
        :pending-exit="config.pending_exit_count || 0"
        :exited-completed="config.exited_completed || 0"
        :exited-timed-out="config.exited_timed_out || 0"
        :exited-disqualified="config.exited_removed || 0"
      />
      <router-link
        v-if="config.sequence_id && playId"
        class="seqLink"
        :to="`/sales-plays/${playId}/sequence`"
        @click.stop
        title="View sequence detail"
      >
        <AppIcon name="arrow_up_right" :size="14" />
      </router-link>
    </div>
    <div v-if="hasSampleEmails" class="detailRow">
      <span class="detailLabel">SAMPLES</span>
      <span class="detailValue">{{ sampleEmails.length }} email{{ sampleEmails.length !== 1 ? 's' : '' }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import AppIcon from '../../../components/AppIcon.vue'
import SequenceSummary from '../../../components/SequenceSummary.vue'

const props = defineProps<{
  config: Record<string, any>
}>()

const route = useRoute()
const playId = computed(() => String(route.params.id || ''))

const hasSteps = computed(() => (props.config.steps || []).length > 0)
const sampleEmails = computed(() => props.config.sample_emails || [])
const hasSampleEmails = computed(() => sampleEmails.value.length > 0)

// Build sparse arrays from sequence_step_stats (preferred) or sequence_step_counts (fallback).
const stepCountsArray = computed(() => {
  const stats = props.config.sequence_step_stats || []
  if (stats.length) {
    const arr: number[] = []
    for (const entry of stats) {
      arr[entry.step_index] = entry.count || 0
    }
    return arr
  }
  // Fallback to play-level counts (no waiting/ready split available).
  const raw = props.config.sequence_step_counts || []
  const arr: number[] = []
  for (const entry of raw) {
    arr[entry.step_index] = entry.count || 0
  }
  return arr
})

const stepReadyCountsArray = computed(() => {
  const stats = props.config.sequence_step_stats || []
  if (!stats.length) return []
  const arr: number[] = []
  for (const entry of stats) {
    arr[entry.step_index] = entry.ready_count || 0
  }
  return arr
})
</script>

<style scoped>
.details {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.detailRow {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
}
.detailLabel {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  color: var(--color-text-tertiary);
  flex-shrink: 0;
}
.seqLink {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: var(--color-text-tertiary);
  transition: color var(--duration-fast) var(--ease-out);
}
.seqLink:hover {
  color: var(--color-accent);
}
.detailValue {
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
}
.detailValue.approach {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
